import Inherit from '../../core/inherit';
import ShelfController from '../../core/shelf.controller';
import { TraditionalStores } from '../common';
import { User } from '../../data';
import DistributorPriceVisualsDelegate from './distributorPrices.delegate';

const ALLOWED_USER_TYPES = ['distributor', 'admin', 'gerente', 'asesor'];

class DistributorPriceVisualsController extends Inherit(ShelfController, DistributorPriceVisualsDelegate) {
    async initialize() {
        if (!this.isAuthenticated) {
            this.setState({ loading: false });
            this.navigateLogin();
            return;
        }

        const user = User.getInstance();
        if (!ALLOWED_USER_TYPES.includes(user?.userType)) {
            this.setState({ loading: false, accessDenied: true });
            return;
        }

        try {
            let priceLists = [];
            try {
                priceLists = (await this.data.fetchPrices()) || [];
            } catch (err) {
                if (err?.response?.status !== 404) throw err;
            }

            const priceOptions = await this.formatPriceOptions(priceLists);
            const defaultType = priceOptions[0]?.value || null;

            this.setState({ priceOptions, selectedType: defaultType, loading: false });

            this.loadAnomalyThreshold();

            if (defaultType) {
                await this.loadVisuals(defaultType, this.state.dateRange);
            }
        } catch (err) {
            this.props.onMessage?.(
                'error',
                err?.response?.data?.message || err?.message || 'Error al cargar listas de precios'
            );
            this.setState({ loading: false, error: true });
        }
    }

    async formatPriceOptions(prices) {
        let clients = [];
        try {
            clients = (await this.data.fetchClients()) || [];
        } catch (err) {
            if (err?.response?.status !== 404) throw err;
        }
        const clientMap = new Map(clients.map((client) => [client._id, client]));

        return (prices || []).map((price) => {
            const traditional = TraditionalStores.find((store) => store.value === price.type);
            const label = traditional?.label || clientMap.get(price.type)?.name || price.type;
            return { value: price.type, label, channel: price.channel };
        });
    }

    async loadVisuals(priceType, dateRange, { preserveFocus = false } = {}) {
        if (!priceType || !dateRange?.[0] || !dateRange?.[1]) return;
        const focusToKeep = preserveFocus ? this.state.focusedProductId : null;
        const idsToKeep = preserveFocus ? this.state.selectedProductIds : [];
        this.setState({ dataLoading: true, visuals: null, loadError: null, selectedProductIds: idsToKeep, focusedProductId: focusToKeep });
        try {
            const response = await this.data.fetchDistributorPriceVisuals(
                priceType,
                dateRange[0].format('YYYY-MM-DD'),
                dateRange[1].format('YYYY-MM-DD')
            );
            if (response?.success === false) {
                const message = response?.message || 'Error al cargar visualización';
                this.setState({ dataLoading: false, loadError: message });
                return;
            }

            const stillExists = focusToKeep && (response.products || []).some((p) => p.productId === focusToKeep);
            const firstWithData = (response.products || []).find((p) => p.observationCount > 0);
            this.setState({
                visuals: response,
                dataLoading: false,
                loadError: null,
                focusedProductId: stillExists ? focusToKeep : firstWithData?.productId || response.products?.[0]?.productId || null,
            });
        } catch (err) {
            const message = err?.response?.data?.message || err?.message || 'Error al cargar visualización';
            this.setState({ dataLoading: false, loadError: message });
        }
    }

    getFilteredProducts(visuals, selectedProductIds) {
        const products = visuals?.products || [];
        if (!selectedProductIds?.length) return products;
        const allowed = new Set(selectedProductIds);
        return products.filter((p) => allowed.has(p.productId));
    }

    async loadAnomalyThreshold() {
        try {
            const res = await this.data.fetchDistributorPriceAnomalyThreshold();
            if (res?.success) {
                const value = res.data?.thresholdPercent;
                this.setState({ anomalyThreshold: value ?? 3, anomalyThresholdDraft: value ?? 3 });
            }
        } catch (err) {
            // Non-fatal: keep default threshold
            if (err?.response?.status !== 404) {
                this.props.onMessage?.('warning', 'No se pudo cargar el umbral de anomalía');
            }
        }
    }

    async saveAnomalyThreshold() {
        const value = Number(this.state.anomalyThresholdDraft);
        if (Number.isNaN(value) || value < 0) {
            this.props.onMessage?.('error', 'Umbral inválido');
            return;
        }
        this.setState({ savingThreshold: true });
        try {
            const res = await this.data.updateDistributorPriceAnomalyThreshold(value);
            if (res?.success) {
                this.props.onMessage?.('success', `Umbral de anomalía actualizado a ${value}%`);
                this.setState({ anomalyThreshold: res.data?.thresholdPercent ?? value });
                // Recompute visuals so suspect flags reflect the new threshold
                if (this.state.selectedType) await this.loadVisuals(this.state.selectedType, this.state.dateRange, { preserveFocus: true });
            } else {
                this.props.onMessage?.('error', res?.message || 'No se pudo guardar el umbral');
            }
        } catch (err) {
            this.props.onMessage?.('error', err?.response?.data?.message || err?.message || 'Error al guardar el umbral');
        } finally {
            this.setState({ savingThreshold: false });
        }
    }

    reviewObservation = async ({ storeCheckId, productId, decision, reason }) => {
        if (!storeCheckId || !productId || !decision) return;
        this.setState({ reviewingObservation: true });
        try {
            const res = await this.data.reviewDistributorPriceObservation({ storeCheckId, productId, decision, reason });
            if (res?.success) {
                const labels = { validated: 'validado', excluded: 'excluido', clear: 'restablecido' };
                this.props.onMessage?.('success', `Registro ${labels[decision] || 'actualizado'}`);
                if (this.state.selectedType) await this.loadVisuals(this.state.selectedType, this.state.dateRange, { preserveFocus: true });
            } else {
                this.props.onMessage?.('error', res?.message || 'No se pudo revisar el registro');
            }
        } catch (err) {
            this.props.onMessage?.('error', err?.response?.data?.message || err?.message || 'Error al revisar el registro');
        } finally {
            this.setState({ reviewingObservation: false });
        }
    };

    getProductFilterOptions(visuals) {
        return (visuals?.products || [])
            .map((p) => ({
                value: p.productId,
                label: p.name,
                observationCount: p.observationCount,
            }))
            .sort((a, b) => {
                if (b.observationCount !== a.observationCount) return b.observationCount - a.observationCount;
                return a.label.localeCompare(b.label);
            });
    }

    // Lista / categoría changed: reset the error and load that list's visuals.
    onPriceTypeChange(v) {
        this.setState({ selectedType: v, loadError: null });
        this.loadVisuals(v, this.state.dateRange);
    }

    // Período changed (ignore half-picked ranges).
    onDateRangeChange(dates) {
        if (!dates?.[0] || !dates?.[1]) return;
        this.setState({ dateRange: dates, loadError: null });
        if (this.state.selectedType) this.loadVisuals(this.state.selectedType, dates);
    }

    retryVisuals() {
        const { selectedType, dateRange } = this.state;
        if (selectedType) this.loadVisuals(selectedType, dateRange, { preserveFocus: true });
    }

    // Everything the view derives from the loaded visuals + the product filter.
    getViewModel() {
        const { priceOptions, selectedType, selectedProductIds, focusedProductId, visuals } = this.state;
        const selectedOption = priceOptions.find((o) => o.value === selectedType);
        const productFilterOptions = visuals ? this.getProductFilterOptions(visuals) : [];
        const filteredProducts = visuals ? this.getFilteredProducts(visuals, selectedProductIds) : [];
        const focusedInFilter =
            focusedProductId && filteredProducts.some((p) => p.productId === focusedProductId)
                ? focusedProductId
                : filteredProducts.find((p) => p.observationCount > 0)?.productId || filteredProducts[0]?.productId || null;
        const productsWithData = filteredProducts.filter((p) => p.observationCount > 0).length;
        const observationTotal = filteredProducts.reduce((s, p) => s + (p.observationCount || 0), 0);
        const reportingStores = visuals?.summary?.reportingStoreCount ?? visuals?.summary?.storeCount ?? 0;
        return {
            selectedOption,
            productFilterOptions,
            filteredProducts,
            focusedInFilter,
            productsWithData,
            observationTotal,
            reportingStores
        };
    }
}

export default DistributorPriceVisualsController;
